import React, {FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Select, SelectProps, TreeSelect} from 'antd';
import {useSearchParams} from "react-router-dom";
import {useQuery} from "react-query";
import {FilterProps} from "../config/routes";

interface OptionProps {
    label: string,
    value: string | number,
    children?: OptionProps[]
}

export interface SearchParamFilterSelectProps<T> extends Omit<SelectProps<T>, 'mode' | 'onChange'> {
    query: string,
    searchParam: string,
    optionMap: (item: T) => OptionProps,
    optionsTransform?: (items: T[], optionMap: (item: T) => OptionProps) => OptionProps[],
    useTreeSelect?: boolean,
    mode?: FilterProps['mode']
}

export const SearchParamFilterSelect: FC<SearchParamFilterSelectProps<any>> = ({query, searchParam, placeholder, mode = 'multiple', optionMap, optionsTransform, useTreeSelect = false, onDropdownVisibleChange = () => {}, dropdownClassName, ...rest}): ReactElement => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [open, setOpen] = useState<boolean>(false);
    const {data, isLoading} = useQuery<object[]>(query);

    // Controls UI
    const [selected, setSelected] = useState<(number | string)[]>(() => {
        return searchParams.get(searchParam)?.split(',').map((id: string) => parseInt(id)) || [];
    });
    // Needed for sync updates when committing values on dropdown close
    // the updates from setSelected aren't applied if mode = 'single' as the dropdown and change happen at the same time
    const selectedRef = useRef<(number | string)[]>(selected);

    // Sync with changes from top filter selection listener
    useEffect(() => {
        const committedValues = searchParams.get(searchParam)?.split(',').map((id: string) => parseInt(id)) || []; 
        if (committedValues.length !== selected.length && committedValues.some((val, i) => val !== selected[i])) {
            setSelected(committedValues);
            selectedRef.current = committedValues;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, searchParam]);

    const commitValues = useCallback((values: (number | string)[]) => {
        // Reset pagination
        const searchParamsList = Object.fromEntries(searchParams.entries());
        for (let key in searchParamsList) {
            if (key.endsWith('page')) {
                searchParams.delete(key);
            }
        }
        // Update values for filter
        if (values.length > 0) {
            searchParams.set(searchParam, values.join(','));
        } else {
            searchParams.delete(searchParam);
        }
        // Commit into search params
        setSearchParams(searchParams, {replace: true});
    }, [searchParam, searchParams, setSearchParams]);
    
    const handleChange = useCallback((values: (number | string)[]) => {
        console.log(values);
        if (!Array.isArray(values)) {
            values = [values]; // Single select
        }
        if (values.includes('all')) {
            values = data?.map(optionMap).map((option) => option.value) || []; 
            setOpen(false);
            onDropdownVisibleChange(false);
            commitValues(values);
        } 
        setSelected(values);
        selectedRef.current = values;
    }, [data, optionMap, commitValues, onDropdownVisibleChange]);

    const options = useMemo(() => {
        const options = optionsTransform
            ? optionsTransform(data || [], optionMap)
            : data?.map(optionMap) || [];
        if (mode !== 'single') {
            return [{value: 'all', label: 'Select All'}, ...options];
        }
        return options;
    }, [mode, data, optionMap, optionsTransform]);

    const treeData = useMemo(() => {
        const toTreeNode = (option: OptionProps): any => ({
            value: option.value,
            title: option.label,
            children: option.children?.map(toTreeNode)
        });
        return options.map(toTreeNode);
    }, [options]);

    if (useTreeSelect) {
        return (
            <TreeSelect
                open={open}
                value={mode === 'single' ? selected[0] : selected}
                treeData={treeData}
                treeDefaultExpandAll
                multiple={mode !== 'single'}
                showSearch
                loading={isLoading}
                allowClear
                onClear={() => commitValues([])}
                showArrow
                placeholder={open ? 'Search...' : placeholder}
                onChange={(values) => handleChange(values)}
                maxTagCount='responsive'
                treeNodeFilterProp='title'
                onDropdownVisibleChange={(open) => {
                    setOpen(open);
                    onDropdownVisibleChange(open);
                    if (!open) commitValues(selectedRef.current);
                }}
                {...rest}
            />
        );
    }

    return (
        <Select
            open={open}
            value={selected}
            mode={mode === 'single' ? undefined : mode}
            showSearch
            loading={isLoading}
            allowClear
            onClear={() => commitValues([])}
            removeIcon={null} // Disable item removal
            showArrow
            defaultActiveFirstOption={false}
            placeholder={open ? 'Search...' : placeholder}
            onChange={handleChange}
            maxTagCount='responsive'
            optionFilterProp='label'
            options={options}
            // Track state of dropdown
            // Needed in order to update query params on dropdown close
            onDropdownVisibleChange={(open) => {
                setOpen(open)
                onDropdownVisibleChange(open);
                if (!open) commitValues(selectedRef.current);
            }}
            {...rest}
        >
        </Select>
    );
};
